import React, { useMemo, useRef } from 'react';
import useResizeObserver from 'Hooks/useResizeObserver';
import ReactECharts from 'echarts-for-react';
import get from 'lodash/get';
import {
  CHART_COLORS,
  INSIGHT_CHART_FONT_CHAR_WIDTH,
} from 'utils/constants/dashboard';
import { formatChartsTooltip } from '../helpers';
import { HorizontalBarChartProps } from '../chartBuilder';
import {
  CHART_EMPTY_STATE_COLORS,
  CHART_STYLING,
} from '../constants/chartColors';
import { horizontalBarEmptyState } from '../constants/chartEmptyStates';
import { InsightDrilldownSelectedData } from '../../../types/store/normalized/insights/insightFilterMetadata';

const GenericHorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  chartData,
  onDrillDown,
  xAxisFieldNames,
  lineFieldNames,
  lineLegendNames,
  yAxisFieldName,
  legendNames,
  stacks,
  drillDownKeys,
  minInterval,
  lineAxisName,
  barAxisName,
  yAxisName,
  xAxisOptions,
  stackTotalFieldName,
  markLines,
  gridPadding,
}) => {
  const containerRef = useRef<any>();
  const { height, width } = useResizeObserver(containerRef);

  const onEvents = useMemo(
    () => ({
      click: (item: {
        seriesType?: string;
        dataIndex?: number;
        seriesIndex?: number;
      }) => {
        let data: InsightDrilldownSelectedData | undefined;

        if (
          yAxisFieldName &&
          item.dataIndex !== undefined &&
          chartData.length > 0
        ) {
          const chartItem = chartData[item.dataIndex];
          const fieldValue = chartItem[yAxisFieldName];

          if (fieldValue) {
            data = {
              filters: { fieldName: yAxisFieldName, fieldValue },
              chartData: chartItem,
            };
          }
        }

        if (onDrillDown && item.seriesType === 'bar') {
          let yAxisDrillKey = get(
            drillDownKeys,
            'categoryAxis.0',
            yAxisFieldName
          ) as string;
          const totalSeries =
            xAxisFieldNames.length + (lineFieldNames?.length || 0);

          let xAxisDrillKey = undefined;
          if (totalSeries > 1) {
            xAxisDrillKey = get(
              drillDownKeys,
              'valueAxis.' + get(item, 'seriesIndex', 0),
              get(xAxisFieldNames, get(item, 'seriesIndex', 0))
            ) as string;
          }

          if (
            xAxisDrillKey &&
            (drillDownKeys?.valueAxis?.includes(xAxisDrillKey) ||
              drillDownKeys?.categoryAxis?.includes(yAxisDrillKey))
          ) {
            onDrillDown(xAxisDrillKey, data);
          } else {
            onDrillDown(undefined, data);
          }
        }
      },
    }),
    [
      chartData,
      drillDownKeys,
      lineFieldNames?.length,
      onDrillDown,
      xAxisFieldNames,
      yAxisFieldName,
    ]
  );

  const options = useMemo(() => {
    // Handle empty data case
    if (chartData.length === 0) {
      return horizontalBarEmptyState();
    }

    let yAxisCharCount = Math.max(
      ...chartData.map(
        (i) => get(i, yAxisFieldName, '').toLocaleString().length
      )
    );

    let yAxisWidth = yAxisCharCount * INSIGHT_CHART_FONT_CHAR_WIDTH;
    let lineAxisHeight = lineAxisName && lineFieldNames?.length ? 24 : 0;
    let barAxisHeight = barAxisName ? 40 : 0;

    const legendHeight =
      xAxisFieldNames.length + (lineFieldNames?.length || 0) > 1 ? 24 : 0;
    let remainingHeight =
      height - legendHeight - lineAxisHeight - barAxisHeight;

    let nonStackedBarCount = xAxisFieldNames.reduce((a, _c, i) => {
      if (stacks?.length && stacks[i] === undefined) return a + 1;
      return a;
    }, 0);

    const barsPerGroup = stacks?.length
      ? new Set(stacks).size + nonStackedBarCount
      : xAxisFieldNames.length;
    const barGap = 4;
    const barSize = 24;
    const groupGap = 16;
    const groupSize =
      groupGap + barSize * barsPerGroup + barGap * (barsPerGroup - 1);

    let numOfBarGroups = Math.min(
      chartData.length,
      Math.floor(remainingHeight / groupSize)
    ); // It should be >= 1.
    let expandedGroupSize = Math.floor(remainingHeight / numOfBarGroups);
    let expandedBarSize = Math.floor(
      (expandedGroupSize - groupGap - barGap * (barsPerGroup - 1)) /
        barsPerGroup
    );

    const dataZoomOptions = [];
    if (numOfBarGroups < chartData.length) {
      dataZoomOptions.push({
        orient: 'vertical',
        type: 'slider',
        startValue: chartData.length - numOfBarGroups,
        endValue: chartData.length - 1,
        width: 0,
        right: 12,
        zoomLock: true,
        showDetail: false,
        moveHandleSize: 12,
        moveHandleStyle: {
          color: CHART_EMPTY_STATE_COLORS.DATA_ZOOM_HANDLE,
          opacity: CHART_STYLING.FULL_OPACITY,
        },
      });
      dataZoomOptions.push({
        type: 'inside',
        orient: 'vertical',
        startValue: 0,
        endValue: Math.max(0, numOfBarGroups - 1),
        zoomLock: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
      });
    }

    const series: unknown[] = xAxisFieldNames.map((f, idx) => ({
      type: 'bar',
      data: chartData.map((i) => get(i, f)),
      minBarWidth: expandedBarSize,
      barCategoryGap: groupGap,
      barGap: `${(barGap / expandedBarSize) * 100}%`,
      name: legendNames.length >= idx ? legendNames[idx] : undefined,
      stack: get(stacks, idx),
      itemStyle: get(stacks, idx)
        ? {
            borderWidth: 1,
            borderColor: CHART_EMPTY_STATE_COLORS.BORDER_WHITE,
          }
        : undefined,
      markLine: markLines
        ? {
            symbol: 'none',
            lineStyle: { color: '#B8350A', type: 'dotted' },
            data: markLines.map((l) => ({ xAxis: l })),
          }
        : undefined,
    }));

    if (stackTotalFieldName) {
      const scatterData = chartData.map((t, i) => {
        return [get(t, stackTotalFieldName), i];
      });
      series.push({
        type: 'scatter',
        data: scatterData,
        symbolSize: 1,
        itemStyle: { color: 'transparent' },
        label: {
          show: true,
          position: 'right',
          formatter: (p: { data: [number, number] }) => `${p.data[0]}%`,
        },
        z: 10,
        silent: true,
        tooltip: { show: false },
      });
    }

    if (lineFieldNames && lineFieldNames.length) {
      series.push(
        ...lineFieldNames.map((i, idx) => ({
          type: 'line',
          data: chartData.map((j) => get(j, i)),
          name: get(lineLegendNames, idx),
          showSymbol: false,
          xAxisIndex: lineAxisHeight ? 1 : 0,
        }))
      );
    }

    const xAxis: unknown[] = [
      {
        type: 'value',
        name: barAxisName,
        nameLocation: 'middle',
        nameGap: barAxisName ? 40 : 0,
        minInterval: minInterval || 0,
        min: xAxisOptions?.min || undefined,
        max: xAxisOptions?.max || undefined,
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          align: xAxisOptions?.label?.align || 'right',
          margin: 0,
          formatter: xAxisOptions?.label?.formatter,
        },
      },
    ];

    if (lineAxisHeight) {
      xAxis.push({
        type: 'value',
        name: lineAxisName,
        nameLocation: 'center',
        minInterval: minInterval || 0,
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
          onZero: false,
        },
        axisLabel: {
          align: 'right',
          margin: 0,
        },
      });
    }

    const gridPaddingOverrides = {
      top: gridPadding?.[0] || 0,
      right: gridPadding?.[1] || 0,
      bottom: gridPadding?.[2] || undefined,
      left: gridPadding?.[3] || 0,
    };
    return {
      dataZoom: dataZoomOptions,
      grid: {
        show: false,
        containLabel: true,
        top: lineAxisHeight ? 16 : gridPaddingOverrides.top,
        bottom: gridPaddingOverrides.bottom || legendHeight + barAxisHeight,
        right:
          numOfBarGroups < chartData.length ? 10 : gridPaddingOverrides.right,
        left: yAxisName ? 40 : gridPaddingOverrides.left,
      },
      xAxis: xAxis,
      yAxis: {
        type: 'category',
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: yAxisName ? 120 : 0,
        data: chartData.map((i) => get(i, yAxisFieldName)),
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          width: yAxisWidth >= 120 ? 120 : undefined,
          overflow: 'truncate',
          margin: 4,
        },
      },
      series: series,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: formatChartsTooltip,
      },
      legend: {
        show: series.length > 1,
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        left: 0,
        textStyle: {
          overflow: 'truncate',
        },
        data: [
          ...legendNames.map((i) => ({
            name: i,
            icon: 'circle',
          })),
          ...(lineLegendNames || []).map((i) => ({
            name: i,
          })),
        ],
      },
      textStyle: {
        fontFamily: 'inherit',
      },
      color: CHART_COLORS,
    };
  }, [
    chartData,
    lineAxisName,
    lineFieldNames,
    xAxisFieldNames,
    height,
    stacks,
    minInterval,
    legendNames,
    lineLegendNames,
    yAxisFieldName,
    stackTotalFieldName,
    barAxisName,
    yAxisName,
    xAxisOptions,
    markLines,
    gridPadding,
  ]);

  return (
    <div className='h-full w-full' ref={containerRef}>
      {width > 0 && (
        <ReactECharts
          key={`${width}-${height}`}
          opts={{ renderer: 'svg' }}
          option={options}
          style={{ height: height, width: width }}
          onEvents={onEvents}
        />
      )}
    </div>
  );
};

export default GenericHorizontalBarChart;
